import {
  Banner,
  BodyText,
  Button,
  Heading,
  Icon,
  IconSet,
  ModalBody,
} from '@postscript/components';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import styled from 'styled-components';

import { BrandCenterRoutes } from 'components/brand/types';
import useDisclaimerText from 'components/popups/editor/hooks/useDisclaimerText';
import { useShopperModals } from 'components/shopper/components/modals/ShopperModalRouter';
import { DisclaimerText } from 'components/shopper/components/ShopperConfigTabs/SettingsTab/DisclaimerText';
import { ShopperDemoQRCode } from 'components/shopper/components/ShopperDemoQRCode';
import { SHOPPER_DEMO_KEYWORD } from 'components/shopper/constants';
import { getDemoShopperModalShownKey } from 'components/shopper/utils/helpers';
import { useUser } from 'controllers/contexts/user';
import gradient from 'img/shopper-gradient.png';
import { useInvalidateOnboardingState } from 'providers/ProgressiveOnboarding/queries';
import {
  COMMON_EVENT_NAMES,
  logButtonClickEvent,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';

const Container = styled(ModalBody)`
  background-color: var(--gray-9);
  display: flex;
`;

const CloseButton = styled(Button)`
  position: absolute;
  right: 1rem;
  top: 1rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
  padding: var(--spacing-6);
  flex: 1;
`;

const MediaSection = styled(Section)`
  background: url(${gradient});
  background-size: cover;
`;

const QRCodeContainer = styled.div`
  flex: 1;
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: auto;
    width: 100%;
    max-width: 258px;
    border-radius: var(--spacing-4);
    border: none;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const CallToAction = styled.div`
  display: flex;
  gap: var(--spacing-3);

  padding: var(--spacing-3) var(--spacing-3) 0 var(--spacing-2);
`;

const StyledBanner = styled(Banner)`
  background-color: var(--gray-7);
  border-color: var(--border-color);

  svg,
  p {
    color: white;
  }

  ul {
    margin-left: var(--spacing-3);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: var(--spacing-2);

  button {
    flex: 1;
  }
`;

const ShopperDemoModal = () => {
  const history = useHistory();
  const { user } = useUser();
  const { hideModal } = useShopperModals();
  const invalidateOnboardingState = useInvalidateOnboardingState();
  const [hasBeenShownDemoModal, setHasBeenShownDemoModal] = useLocalStorage(
    getDemoShopperModalShownKey(user.shop_id),
  );
  const { data: disclaimerData } = useDisclaimerText(user.shop_id);

  // Flag that the modal has been shown, so it's not automatically shown later
  useEffect(() => {
    if (!hasBeenShownDemoModal) {
      setHasBeenShownDemoModal(true);
    }
  }, [hasBeenShownDemoModal, setHasBeenShownDemoModal]);

  // Check the server every 10 seconds to see if the user has texted the demo number
  useEffect(() => {
    const interval = setInterval(invalidateOnboardingState, 10_000);

    return () => clearInterval(interval);
  }, [invalidateOnboardingState]);

  const heading = 'Demo Shopper';
  const source = 'shopper demo modal';

  const handleClose = () => {
    logEvent(COMMON_EVENT_NAMES.BUTTON_CLICKED, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
      text: 'Close',
    });

    hideModal();
  };

  const onClickCustomize = (event: React.MouseEvent<HTMLButtonElement>) => {
    logButtonClickEvent(event, {
      modal_heading: heading,
      modal_type: MODAL_TYPES.SHOPPER,
      product_area: PRODUCT_AREAS.SHOPPER,
      shop_id: user.shop_id,
      source,
    });

    hideModal();

    history.push(BrandCenterRoutes.SHOPPER);
  };

  return (
    <Container data-theme="dark">
      <CloseButton variant="plain" onClick={handleClose} icon={IconSet.Close} />

      <MediaSection>
        <QRCodeContainer>
          <ShopperDemoQRCode shopId={user.shop_id} />
        </QRCodeContainer>

        {disclaimerData && (
          <BodyText size="x-small">
            <DisclaimerText
              disclaimer={disclaimerData}
              shopDomain={user.shop}
            />
          </BodyText>
        )}
      </MediaSection>

      <Section>
        <Header>
          <Heading size="large">{heading}</Heading>
          <BodyText color="var(--text-color)">
            Now that you have Brand Center set up, you can text a version of
            your shopper! Demo how Shopper might engage with customers in
            real-time.
          </BodyText>
        </Header>

        <CallToAction>
          <Icon component={IconSet.Iphone} color="var(--blue-2)" />
          <div>
            <Heading size="x-small">To Start Your Demo</Heading>
            <BodyText color="var(--text-color)">
              Scan the <strong>QR code with your phone</strong> or{' '}
              <strong>text {SHOPPER_DEMO_KEYWORD}</strong> to your shop number.
            </BodyText>
          </div>
        </CallToAction>

        <StyledBanner
          data-theme="light"
          variant="guidance"
          bodyText={
            <>
              For a more brand-aligned experience,
              <ul>
                <li>Adjust your Shopper&apos;s settings</li>
                <li>Update Brand Center to refine Voice & Tone</li>
                <li>Be aware of Shopper product catalog content limits</li>
              </ul>
            </>
          }
        />
        <BodyText color="var(--text-color)">
          <strong>Looking for Real Results?</strong> Ensure your Shopper is
          activated to engage real customers and see the impact!
        </BodyText>

        <Actions>
          <Button
            onClick={onClickCustomize}
            data-testid="customize-shopper-button"
          >
            Customize Shopper
          </Button>
        </Actions>
      </Section>
    </Container>
  );
};

export default ShopperDemoModal;
